import { useRef } from 'react'
import { Lang } from '@/components/ui'
import { hero } from '@/content/site'
import { EASE, gsap, useGSAP } from '@/lib/gsap'
import styles from './Hero.module.css'
import type { HeroState } from './state'
import { noise1 } from './voice'

const BARS = [0.75, 1, 0.6]

/** Props for {@link SpeakChip}. */
export interface SpeakChipProps {
  state: HeroState
  reducedMotion: boolean
}

/**
 * A small glass chip beside "Talk." that cycles `hero.speakWords` (decorative, aria-hidden;
 * the H1 stays the real text). Words slide through a mask on a slightly irregular beat, and
 * three tiny bars follow the orb's voice. Reduced motion: one still word, still bars.
 */
export function SpeakChip({ state, reducedMotion }: SpeakChipProps) {
  const rootRef = useRef<HTMLSpanElement>(null)

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return
      const words = gsap.utils.toArray<HTMLElement>('[data-word]', root)
      const mask = root.querySelector<HTMLElement>('[data-mask]')
      const bars = gsap.utils.toArray<HTMLElement>('[data-bar]', root)
      if (!mask || words.length === 0) return

      const show = (index: number) => {
        gsap.set(words, { yPercent: 110, autoAlpha: 0 })
        gsap.set(words[index], { yPercent: 0, autoAlpha: 1 })
        gsap.set(mask, { width: words[index].offsetWidth })
      }

      if (reducedMotion) {
        show(Math.min(1, words.length - 1))
        return
      }

      let index = 0
      show(index)
      let call = gsap.delayedCall(2.8, function next() {
        const outgoing = words[index]
        index = (index + 1) % words.length
        const incoming = words[index]
        gsap.to(outgoing, { yPercent: -110, autoAlpha: 0, duration: 0.95, ease: EASE.inOut })
        gsap.fromTo(
          incoming,
          { yPercent: 110, autoAlpha: 0 },
          { yPercent: 0, autoAlpha: 1, duration: 1, delay: 0.08, ease: EASE.inOut },
        )
        gsap.to(mask, { width: incoming.offsetWidth, duration: 0.9, ease: EASE.inOut })
        // A slightly irregular beat, like speech, never a metronome.
        call = gsap.delayedCall(2.55 + Math.random() * 0.6, next)
      })

      const tick = (time: number) => {
        const level = state.speaking
        bars.forEach((bar, i) => {
          const jitter = 0.75 + 0.25 * noise1(time * 3.1 + i * 7.3, 101)
          bar.style.transform = `scaleY(${(0.28 + level * BARS[i] * jitter * 0.72).toFixed(3)})`
        })
      }
      gsap.ticker.add(tick)
      return () => {
        call.kill()
        gsap.ticker.remove(tick)
      }
    },
    { scope: rootRef, dependencies: [reducedMotion, state], revertOnUpdate: true },
  )

  return (
    <span ref={rootRef} className={styles.chip} aria-hidden="true" data-intro="chip">
      <span className={styles.bars}>
        {BARS.map((_, i) => (
          <span key={i} className={styles.bar} data-bar />
        ))}
      </span>
      <span className={styles.mask} data-mask>
        {hero.speakWords.map((word) => (
          <span key={word.text} className={styles.word} data-word>
            <Lang code={word.lang}>{word.text}</Lang>
          </span>
        ))}
      </span>
    </span>
  )
}
